import { keyframes } from "@mui/material";
import { CSSObject, Theme } from "@mui/material/styles";
import { brandColors, buttonTokens, cautionColors } from "@trusty/shared";
import { DARK_GRADIENT_BACKGROUND } from "./theme";

export const FONT_HEADING = "'Outfit', sans-serif";
export const FONT_BODY = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
// Account app-tier button tokens, sourced from @trusty/shared (ENG-2382) so no hardcoded button hex
// can drift. EDEN keeps its name (widely imported) but now derives from the shared app token.
export const EDEN = buttonTokens.app.background;            // #0F5257 (Eden)
export const EDEN_HOVER = buttonTokens.app.backgroundHover; // #0A3D41 (canonical dark-Eden hover)
export const DANGER = brandColors.red;                      // #A40000 (the one brand Red)
export const CAUTION = cautionColors.caution;               // #D97706 (sanctioned amber)
export const RADIUS = { pill: '100px', cta: '38px', card: '12px', input: '12px', dialog: '20px' };
export const HOVER_LIFT = { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' };
export const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: FONT_BODY,
    borderRadius: RADIUS.input,
    backgroundColor: '#F8F8F8',
    '& fieldset': { borderColor: '#F7F0F5' },
    '&:hover fieldset': { borderColor: EDEN },
    '&.Mui-focused fieldset': { borderColor: EDEN, boxShadow: `0 0 0 3px rgba(15, 82, 87, 0.1)` },
  },
  '& .MuiInputLabel-root': { fontFamily: FONT_BODY },
};
export const dialogPaperProps = { sx: { borderRadius: RADIUS.dialog } };

export const GRADIENT_BACKGROUND = `
  radial-gradient(ellipse 1200px 1000px at 20% 60%, rgba(26, 122, 130, 0.6) 0%, transparent 50%),
  radial-gradient(ellipse 1000px 900px at 80% 40%, rgba(198, 167, 210, 0.5) 0%, transparent 55%),
  radial-gradient(ellipse 900px 800px at 10% 20%, rgba(2, 68, 59, 0.4) 0%, transparent 50%),
  radial-gradient(ellipse 1100px 1000px at 90% 80%, rgba(229, 192, 223, 0.45) 0%, transparent 55%),
  radial-gradient(ellipse 1300px 1100px at 50% 100%, rgba(163, 231, 174, 0.3) 0%, transparent 60%),
  linear-gradient(135deg, #5DBDB8 0%, ${brandColors.mint} 50%, #E5C0DF 100%)
`;

export const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(122, 220, 163, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 30px 10px rgba(122, 220, 163, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(122, 220, 163, 0);
  }
`;

// ── Theme-aware factories ──

export const getTextFieldSx = (theme: Theme) => ({
  '& .MuiOutlinedInput-root': {
    fontFamily: FONT_BODY,
    borderRadius: RADIUS.input,
    backgroundColor: theme.palette.custom.inputBg,
    color: theme.palette.text.primary,
    '& fieldset': { borderColor: theme.palette.custom.inputBorder },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${theme.palette.mode === 'dark' ? 'rgba(26,122,130,0.2)' : 'rgba(15,82,87,0.1)'}`,
    },
  },
  '& .MuiInputLabel-root': { fontFamily: FONT_BODY, color: theme.palette.text.secondary },
});

export const getCardSx = (theme: Theme) => ({
  borderRadius: RADIUS.card,
  border: `1px solid ${theme.palette.custom.cardBorder}`,
  boxShadow: theme.palette.custom.cardShadow,
});

export const getHoverLift = (theme: Theme) => ({
  transform: 'translateY(-1px)',
  boxShadow: theme.palette.custom.hoverShadow,
});

// ENG-2530 (direction B — "Warm canvas"): a softened, top-anchored echo of the login
// gradient, layered over theme.background.default. Every colour is a brand token
// (Mint/Elm/Algae light; Elm/Forest/Plum dark) at low alpha, so the console reads
// unmistakably Haven while staying a calm data surface. Amplifies the prior whisper radial.
export const getDashboardBackground = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? `radial-gradient(120% 80% at 50% 0%, ${brandColors.elm}33 0%, transparent 55%),
       radial-gradient(85% 60% at 0% 8%, ${brandColors.forest}66 0%, transparent 50%),
       radial-gradient(85% 60% at 100% 12%, ${brandColors.plum}40 0%, transparent 55%)`
    : `radial-gradient(120% 80% at 50% 0%, ${brandColors.mint}5C 0%, transparent 55%),
       radial-gradient(85% 55% at 100% 6%, ${brandColors.elm}22 0%, transparent 50%),
       radial-gradient(85% 60% at 0% 12%, ${brandColors.algaeGreen}1F 0%, transparent 50%)`;

export const getLoginGradient = (theme: Theme) =>
  theme.palette.mode === 'dark' ? DARK_GRADIENT_BACKGROUND : GRADIENT_BACKGROUND;

export const getNavLinkStyle = (theme: Theme) => ({
  fontFamily: FONT_BODY,
  fontWeight: 600,
  fontSize: '14px',
  color: theme.palette.mode === 'dark' ? '#FFFFFF' : brandColors.forest,
  textDecoration: 'underline' as const,
  cursor: 'pointer',
});

export const FIXED_ALERT_SX = {
  position: 'fixed' as const,
  top: 90,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1400,
  maxWidth: 500,
  boxShadow: 3,
};

/**
 * ENG-3476: the 14/30/90-day window toggle (Risk Report, SaaS & AI Inventory) - a ButtonGroup whose
 * chosen period is `contained` and the others `outlined`.
 *
 * In dark mode MUI draws an outlined button's label in `primary.main`, which there is Elm (#1A7A82):
 * about 3.7:1 on the dark canvas and 3:1 where its Elm tint is strongest, under the 4.5:1 WCAG AA asks
 * of text. The periods you were not looking at were the hard ones to read. They take the heading
 * colour (Mint, above 10:1 on either) and the outlined-button border token instead, as Settings'
 * outlined buttons do. Light mode is left alone: Eden on the light canvas is already above 7:1.
 */
export const getPeriodToggleSx = (theme: Theme): CSSObject =>
  theme.palette.mode === 'dark' && theme.palette.custom
    ? {
        '& .MuiButton-outlined': {
          color: theme.palette.custom.headingColor,
          borderColor: theme.palette.custom.outlinedBtnBorder,
          '&:hover': { borderColor: theme.palette.custom.headingColor },
        },
      }
    : {};
